<script setup lang="ts">
import { computed, PropType, ref } from "vue";
import { useBaseStore } from "@/stores/base";
import { matchesPassword } from "@/ui/validation/validationRules";

import { useI18n } from "vue-i18n";
import { usePasswordValidation } from "@/ui/composables/usePasswordValidation";
const { t } = useI18n();

const props = defineProps({
	asyncUpdateFunction: {
		type: Function,
		required: true,
	},
	isProfilePage: {
		type: Boolean,
		default: false,
	},
});

const isDialogVisible = defineModel<boolean>();

const currentPassword = ref("");
const showCurrentPassword = ref(false);
const newPassword = ref("");
const showNewPassword = ref(false);
const newPasswordConfirmation = ref("");
const showNewPasswordConfirmation = ref(false);

const store = useBaseStore();
const { password_requirements, minimal_password_length } =
	store.currentEnvironment;

const { totalScore, passwordRules, zxcvbnChallange } = usePasswordValidation(
	newPassword,
	{
		password_requirements,
		minimal_password_length,
	}
);

const updatePassword = async () => {
	await props.asyncUpdateFunction({
		current_password: currentPassword.value,
		password: newPassword.value,
		password_confirmation: newPasswordConfirmation.value,
	});
};

const isFormValid = ref(false);
</script>

<template>
	<v-dialog v-model="isDialogVisible" width="40vw">
		<v-card elevation="2">
			<v-toolbar>
				<v-toolbar-title>
					<v-icon class="mr-3">mdi-form-textbox-password</v-icon>
					Update Password</v-toolbar-title
				>
				<v-spacer></v-spacer>

				<v-btn icon @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider style="border-color: unset !important" />
			<v-form v-model="isFormValid">
				<v-card-text class="pa-5 mt-0">
					<v-row v-if="props.isProfilePage">
						<v-col>
							<v-label
								class="text-subtitle-1 font-weight-semibold pb-2 text-lightText"
								>Current password</v-label
							>
							<VTextField
								variant="filled"
								v-model="currentPassword"
								:append-inner-icon="
									showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'
								"
								hide-details
								:type="showCurrentPassword ? 'text' : 'password'"
								@click:append-inner="showCurrentPassword = !showCurrentPassword"
							></VTextField>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="6">
							<div>
								<v-label
									class="text-subtitle-1 font-weight-semibold pb-2 text-lightText"
									>New password</v-label
								>
								<VTextField
									variant="filled"
									v-model="newPassword"
									:rules="passwordRules"
									:append-inner-icon="
										showNewPassword ? 'mdi-eye' : 'mdi-eye-off'
									"
									hide-details="auto"
									:type="showNewPassword ? 'text' : 'password'"
									@click:append-inner="showNewPassword = !showNewPassword"
								></VTextField>
							</div>
							<div v-if="newPassword.length > 0">
								<div class="d-flex">
									<v-progress-linear
										class="my-2"
										:color="
											totalScore < 60
												? 'error'
												: totalScore === 100 && zxcvbnChallange?.score === 4
												? 'success'
												: 'warning'
										"
										v-model="totalScore"
										:max="100"
									></v-progress-linear>
									<div class="text-caption ml-3 font-weight-bold">
										{{ zxcvbnChallange?.score === 4 ? "Strong" : "Weak" }}
									</div>
								</div>
								<div
									v-if="
										zxcvbnChallange?.score < 4 &&
										zxcvbnChallange?.feedback?.suggestions.length > 0
									"
								>
									<div class="text-caption font-weight-bold">Suggestions:</div>
									<div
										v-for="suggestion in zxcvbnChallange?.feedback?.suggestions"
										class="text-caption ml-3"
									>
										- {{ suggestion }}
									</div>
								</div>
							</div>
						</v-col>
						<v-col cols="6">
							<v-label
								class="text-subtitle-1 font-weight-semibold pb-2 text-lightText"
								>New password ( confirmation )</v-label
							>
							<VTextField
								variant="filled"
								v-model="newPasswordConfirmation"
								:rules="[matchesPassword(t, newPassword)]"
								:append-inner-icon="
									showNewPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'
								"
								hide-details="auto"
								:type="showNewPasswordConfirmation ? 'text' : 'password'"
								@click:append-inner="
									showNewPasswordConfirmation = !showNewPasswordConfirmation
								"
							></VTextField>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
					<v-btn
						variant="flat"
						color="secondary"
						:disabled="isFormValid === false"
						@click="updatePassword"
						>Update Password</v-btn
					>
				</v-card-actions>
			</v-form>
		</v-card>
	</v-dialog>
</template>

<style lang="scss"></style>
